import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Mic,
  ArrowRight,
  TrendingUp,
  Hash,
  Search,
  Brain,
  X,
  Check,
  Sparkles,
} from 'lucide-react';
import { motion } from 'framer-motion';

/* ─── Animation variants ─── */

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.15 } },
};

const growRight = {
  hidden: { width: '0%' },
  visible: (width: string) => ({
    width,
    transition: { duration: 0.8, ease: 'easeOut' as const },
  }),
};

/* ─── Data ─── */

const speedData = [
  { label: '音声入力', range: '200〜300文字/分', width: '100%', highlight: true },
  { label: 'PCタイピング', range: '100〜150文字/分', width: '50%', highlight: false },
  { label: 'スマホ入力', range: '50〜90文字/分', width: '30%', highlight: false },
  { label: '手書き', range: '40〜60文字/分', width: '20%', highlight: false },
];

const proofValues = [
  {
    icon: TrendingUp,
    title: '感情トレンド',
    desc: '気分の波をグラフで可視化。自分でも気づかなかった心のリズムが見えてきます。',
  },
  {
    icon: Hash,
    title: 'テーマの可視化',
    desc: '繰り返し語るテーマをAIが検出。あなたが本当に大切にしていることが浮かび上がります。',
  },
  {
    icon: Search,
    title: '意味で探せる検索',
    desc: 'キーワードが完全一致しなくても、AIが意味を読み取って関連する日記を見つけます。',
  },
];

/* ─── Insight Mock Card ─── */

/* ─── Dashboard Mock Card (感情の推移 / 気分の分布) ─── */

const dashTabs = ['感情の推移', '気分の分布'] as const;

function DashboardMockCard() {
  const [activeTab, setActiveTab] = useState<(typeof dashTabs)[number]>('感情の推移');

  return (
    <div className="min-w-[80vw] snap-center sm:min-w-0">
      <Card className="h-full rounded-xl shadow-sm hover:shadow-md transition-shadow bg-card overflow-hidden">
        <div className="px-5 pt-5 pb-3 space-y-3">
          {/* Tab + Period selector */}
          <div className="flex items-center justify-between">
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
              {dashTabs.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`text-[10px] px-2.5 py-1 rounded-md transition-colors ${
                    activeTab === tab
                      ? 'bg-white dark:bg-gray-700 font-medium text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-0.5">
              {['1w', '2w', '4w'].map((p) => (
                <span
                  key={p}
                  className={`text-[9px] px-1.5 py-0.5 rounded ${
                    p === '2w'
                      ? 'bg-indigo-600 text-white font-medium'
                      : 'text-muted-foreground'
                  }`}
                >
                  {p}
                </span>
              ))}
            </div>
          </div>

          {/* 感情の推移 */}
          {activeTab === '感情の推移' && (
            <div>
              <div className="flex gap-1">
                <div className="flex flex-col justify-between text-[9px] text-muted-foreground py-0.5 shrink-0">
                  <span>+</span>
                  <span>0</span>
                  <span>-</span>
                </div>
                <div className="flex-1">
                  <svg viewBox="0 0 200 70" className="w-full h-20" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="rgb(99,102,241)" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="rgb(99,102,241)" stopOpacity="0.02" />
                      </linearGradient>
                    </defs>
                    <line x1="0" y1="35" x2="200" y2="35" stroke="rgb(229,231,235)" strokeWidth="0.5" strokeDasharray="3 3" />
                    <path d="M0,15 L20,40 L40,38 L65,25 L85,22 L105,23 L130,40 L150,18 L170,20 L185,18 L200,42 L200,70 L0,70Z" fill="url(#lineGrad)" />
                    <polyline points="0,15 20,40 40,38 65,25 85,22 105,23 130,40 150,18 170,20 185,18 200,42" fill="none" stroke="rgb(99,102,241)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    {[{x:0,y:15},{x:20,y:40},{x:40,y:38},{x:65,y:25},{x:85,y:22},{x:105,y:23},{x:130,y:40},{x:150,y:18},{x:170,y:20},{x:185,y:18},{x:200,y:42}].map((p, i) => (
                      <circle key={i} cx={p.x} cy={p.y} r="2.5" fill="white" stroke="rgb(99,102,241)" strokeWidth="1.5" />
                    ))}
                  </svg>
                </div>
              </div>
              <div className="flex justify-between text-[9px] text-muted-foreground pl-4">
                <span>2/11</span><span>2/18</span><span>2/22</span>
              </div>
            </div>
          )}

          {/* 気分の分布 */}
          {activeTab === '気分の分布' && (
            <div className="flex items-center gap-4 py-2">
              <div className="relative w-20 h-20 shrink-0">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                  <circle cx="18" cy="18" r="14" fill="none" stroke="rgb(59,130,246)" strokeWidth="4" strokeDasharray="29 88" strokeDashoffset="0" />
                  <circle cx="18" cy="18" r="14" fill="none" stroke="rgb(16,185,129)" strokeWidth="4" strokeDasharray="19 88" strokeDashoffset="-29" />
                  <circle cx="18" cy="18" r="14" fill="none" stroke="rgb(249,115,22)" strokeWidth="4" strokeDasharray="10 88" strokeDashoffset="-48" />
                  <circle cx="18" cy="18" r="14" fill="none" stroke="rgb(244,63,94)" strokeWidth="4" strokeDasharray="10 88" strokeDashoffset="-58" />
                  <circle cx="18" cy="18" r="14" fill="none" stroke="rgb(139,92,246)" strokeWidth="4" strokeDasharray="10 88" strokeDashoffset="-68" />
                  <circle cx="18" cy="18" r="14" fill="none" stroke="rgb(6,182,212)" strokeWidth="4" strokeDasharray="10 88" strokeDashoffset="-78" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-lg font-bold leading-none">9</span>
                  <span className="text-[8px] text-muted-foreground">件</span>
                </div>
              </div>
              <div className="space-y-1 text-[10px]">
                <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-500" />😰 3</div>
                <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" />😌 2</div>
                <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-orange-500" />😢 1</div>
                <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-rose-500" />😊 1</div>
                <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-violet-500" />🤩 1</div>
                <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-cyan-500" />🤩 1</div>
              </div>
            </div>
          )}
        </div>
        <CardContent className="pt-3 space-y-2">
          <h3 className="text-lg font-semibold">感情トレンド</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">気分の波をグラフで可視化。自分でも気づかなかった心のリズムが見えてきます。</p>
        </CardContent>
      </Card>
    </div>
  );
}

const insightTabs = ['サマリー', '気づき', 'つながり'] as const;

function InsightMockCard() {
  const [activeTab, setActiveTab] = useState<(typeof insightTabs)[number]>('つながり');

  return (
    <div className="min-w-[80vw] snap-center sm:min-w-0">
      <Card className="h-full rounded-xl shadow-sm hover:shadow-md transition-shadow bg-card overflow-hidden">
        <div className="px-5 pt-5 pb-3 space-y-3">
          {/* Tab UI */}
          <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
            {insightTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 text-center text-[10px] py-1 rounded-md transition-colors ${
                  activeTab === tab
                    ? 'bg-indigo-600 text-white font-medium'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* サマリー */}
          {activeTab === 'サマリー' && (
            <div className="rounded-lg bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border border-amber-200/50 dark:border-amber-800/30 p-3">
              <p className="text-[11px] leading-[1.8] text-amber-900/80 dark:text-amber-200/80">
                週の前半は仕事の負荷が高く、疲れやプレッシャーに関する記述が目立ちます。一方、木曜あたりから「散歩」「読書」など意識的にリフレッシュの時間を確保しており、週末に向けて感情スコアが回復しています。
              </p>
            </div>
          )}

          {/* 気づき */}
          {activeTab === '気づき' && (
            <div className="rounded-lg bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border border-amber-200/50 dark:border-amber-800/30 p-3">
              <p className="text-[11px] leading-[1.8] text-amber-900/80 dark:text-amber-200/80">
                「人と話した日」の翌日は感情スコアが平均0.15高い傾向があります<span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded text-[9px] bg-amber-200/60 dark:bg-amber-800/40 text-amber-700 dark:text-amber-300 mx-0.5">1</span><span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded text-[9px] bg-amber-200/60 dark:bg-amber-800/40 text-amber-700 dark:text-amber-300 mx-0.5">3</span>。ひとりで考え込むより、誰かに話すことで気持ちが整理されているのかもしれません。
              </p>
            </div>
          )}

          {/* つながり */}
          {activeTab === 'つながり' && (
            <div className="rounded-lg bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/20 dark:to-purple-950/20 border border-violet-200/50 dark:border-violet-800/30 p-3 space-y-2">
              <div className="space-y-0.5">
                <p className="text-[10px] text-muted-foreground">3/2</p>
                <p className="text-[11px] font-semibold text-violet-700 dark:text-violet-300">朝の散歩で気づいた小さな変化</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-px bg-violet-200 dark:bg-violet-800" />
                <span className="text-[10px] font-bold text-violet-500">68%</span>
                <div className="flex-1 h-px bg-violet-200 dark:bg-violet-800" />
              </div>
              <div className="space-y-0.5">
                <p className="text-[10px] text-muted-foreground">2/15</p>
                <p className="text-[11px] font-semibold text-violet-700 dark:text-violet-300">なぜか気分が軽かった日</p>
              </div>
              <p className="text-[10px] leading-relaxed text-violet-500 dark:text-violet-400">
                → 日常の小さな発見が気分を上げるきっかけになっている
              </p>
            </div>
          )}
        </div>
        <CardContent className="pt-3 space-y-2">
          <h3 className="text-lg font-semibold">AIの分析</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">AIが思考のパターンや過去のエントリ同士の隠れたつながりを発見してくれます。</p>
        </CardContent>
      </Card>
    </div>
  );
}

/* ─── Page ─── */

export default function LandingPage() {
  useEffect(() => {
    window.dataLayer?.push({
      event: 'virtual_page_view',
      page_path: '/',
      page_title: 'ランディングページ',
    });
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      {/* ─── Header ─── */}
      <header className="fixed top-0 inset-x-0 z-50 glass dark:bg-gray-950/70 dark:backdrop-blur-md border-b">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2">
            <img src="/logo.svg" alt="Hitorigoto" className="h-10 dark:hidden" />
            <img src="/logo-dark.svg" alt="Hitorigoto" className="h-10 hidden dark:block" />
          </div>
          <Link to="/login">
            <Button size="sm" variant="ghost">ログイン</Button>
          </Link>
        </div>
      </header>

      {/* ═══════════════════════════════════════════
          01 — Hook：「自分を知る」に、日記はいらない。
      ═══════════════════════════════════════════ */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        {/* Light mesh */}
        <div
          className="absolute inset-0 dark:hidden pointer-events-none"
          style={{ background: `
            radial-gradient(ellipse 80% 60% at 10% 20%, rgba(196,181,253,0.3), transparent),
            radial-gradient(ellipse 60% 80% at 85% 75%, rgba(249,168,212,0.25), transparent),
            radial-gradient(ellipse 70% 50% at 50% 10%, rgba(165,180,252,0.2), transparent),
            white
          `}}
        />
        {/* Dark mesh */}
        <div
          className="absolute inset-0 hidden dark:block pointer-events-none"
          style={{ background: `
            radial-gradient(ellipse 80% 60% at 10% 20%, rgba(88,28,135,0.15), transparent),
            radial-gradient(ellipse 60% 80% at 85% 75%, rgba(112,26,117,0.1), transparent),
            radial-gradient(ellipse 70% 50% at 50% 10%, rgba(67,56,202,0.1), transparent),
            rgb(3,7,18)
          `}}
        />

        <motion.div
          className="relative max-w-xl mx-auto text-center space-y-8"
          initial="hidden"
          animate="visible"
          variants={stagger}
        >
          <motion.h1
            variants={fadeUp}
            className="text-3xl sm:text-5xl font-bold tracking-tight leading-[1.4] sm:leading-[1.3]"
          >
            話すだけで、<br />
            <span className="text-gradient">自分が見えてくる。</span>
          </motion.h1>

          <div className="text-base sm:text-lg text-muted-foreground leading-relaxed space-y-1">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              日記は続かなかった。
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.2 }}
            >
              でも、Hitorigotoなら——
            </motion.p>
          </div>

          <motion.div variants={fadeUp} className="flex items-center justify-center -ml-4 sm:ml-0">
            {/* Male illustration */}
            <img
              src="/images/hero-voice-male.png"
              alt="スマホに話しかける男性のイラスト"
              loading="lazy"
              className="w-[55%] sm:w-80 object-contain shrink-0 -mr-6 sm:-mr-4"
            />
            {/* Phone mockup — matches actual immersive recording UI */}
            <div className="relative w-[130px] sm:w-52 shrink-0 rounded-[1.5rem] sm:rounded-[2.5rem] border-4 sm:border-[6px] border-gray-800 dark:border-gray-600 bg-black shadow-2xl overflow-hidden">
              {/* Notch */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 sm:w-24 h-3 sm:h-5 bg-gray-800 dark:bg-gray-600 rounded-b-xl sm:rounded-b-2xl z-10" />
              {/* Screen content */}
              <div className="pt-5 sm:pt-8 pb-1.5 sm:pb-3 px-2 sm:px-4 bg-black min-h-[190px] sm:min-h-[340px] flex flex-col">
                {/* Transcription area — top, text hierarchy */}
                <div className="flex-1 flex flex-col justify-end px-0.5 sm:px-1 mb-2 sm:mb-3 text-center">
                  <p className="text-[6px] sm:text-[9px] text-white/30 leading-relaxed">今日はちょっと疲れたな。</p>
                  <p className="text-[6px] sm:text-[9px] text-white/40 leading-relaxed">仕事でいろいろあって、</p>
                  <p className="text-[7px] sm:text-[10px] text-white font-semibold leading-relaxed">帰り道の夕焼けがきれいで</p>
                  <div className="flex items-center justify-center gap-[1px]">
                    <span className="text-[7px] sm:text-[10px] text-indigo-400/80 font-medium">気持ちが軽く</span>
                    <span className="inline-block w-[1px] h-2 sm:h-3 bg-indigo-500 animate-pulse" />
                  </div>
                </div>
                {/* Mirrored waveform — centered bars */}
                <div className="flex items-center justify-center gap-[1px] sm:gap-[1.5px] h-5 sm:h-10 mb-2 sm:mb-3">
                  {[30, 55, 80, 100, 70, 90, 55, 40, 85, 50, 30, 65, 95, 70, 45, 55, 80, 45, 30, 20].map((h, i) => (
                    <div
                      key={i}
                      className="w-[1.5px] sm:w-[2.5px] rounded-full bg-indigo-500 animate-wave"
                      style={{ height: `${Math.max(h, 10)}%`, animationDelay: `${i * 80}ms` }}
                    />
                  ))}
                </div>
                {/* Record button — rounded-2xl with glow rings */}
                <div className="flex justify-center mb-1.5 sm:mb-2">
                  <div className="relative flex items-center justify-center">
                    <div className="absolute w-9 h-9 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-indigo-500/20 animate-ping" style={{ animationDuration: '2s' }} />
                    <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-indigo-600 flex items-center justify-center relative z-10 shadow-lg shadow-indigo-500/30">
                      {/* Pause icon (two bars) */}
                      <div className="flex gap-[2px] sm:gap-1">
                        <div className="w-[2px] h-2.5 sm:w-[3px] sm:h-4 rounded-sm bg-white/90" />
                        <div className="w-[2px] h-2.5 sm:w-[3px] sm:h-4 rounded-sm bg-white/90" />
                      </div>
                    </div>
                  </div>
                </div>
                {/* Bottom toolbar: Cancel | Timer | Done */}
                <div className="flex items-center justify-between px-0.5 sm:px-1">
                  <div className="w-5 h-5 sm:w-8 sm:h-8 rounded-md sm:rounded-lg bg-red-500/15 flex items-center justify-center">
                    <span className="text-[7px] sm:text-[10px] text-red-400 font-medium">✕</span>
                  </div>
                  <span className="font-mono text-[9px] sm:text-sm text-white/70 tracking-wider">01:23</span>
                  <div className="w-5 h-5 sm:w-8 sm:h-8 rounded-md sm:rounded-lg bg-indigo-500/15 flex items-center justify-center">
                    <span className="text-[7px] sm:text-[10px] text-indigo-400 font-medium">✓</span>
                  </div>
                </div>
              </div>
              {/* Home indicator */}
              <div className="h-3 sm:h-5 bg-black flex items-center justify-center">
                <div className="w-10 sm:w-20 h-0.5 sm:h-1 rounded-full bg-gray-700" />
              </div>
            </div>
          </motion.div>

          <motion.p variants={fadeUp} className="text-sm text-muted-foreground">
            声が、ストレスフリーで<span className="font-semibold text-foreground">文字</span>になる。
          </motion.p>

          <motion.div variants={fadeUp}>
            <Link to="/login">
              <Button size="lg" className="gap-2 text-base px-10 rounded-full shadow-md bg-indigo-600 hover:bg-indigo-700 text-white">
                音声認識の精度を試してみる
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* ═══════════════════════════════════════════
          02 — Pain：手段ばかり追いかけていませんか？
      ═══════════════════════════════════════════ */}
      <section className="relative py-20 px-4 overflow-hidden">
        {/* Light mesh */}
        <div
          className="absolute inset-0 dark:hidden pointer-events-none"
          style={{ background: `
            radial-gradient(ellipse 90% 50% at 50% 0%, rgba(196,181,253,0.25), transparent),
            radial-gradient(ellipse 50% 60% at 80% 50%, rgba(232,121,249,0.2), transparent),
            radial-gradient(ellipse 70% 50% at 30% 90%, rgba(249,168,212,0.2), transparent),
            white
          `}}
        />
        {/* Dark mesh */}
        <div
          className="absolute inset-0 hidden dark:block pointer-events-none"
          style={{ background: `
            radial-gradient(ellipse 90% 50% at 50% 0%, rgba(88,28,135,0.15), transparent),
            radial-gradient(ellipse 50% 60% at 80% 50%, rgba(112,26,117,0.1), transparent),
            rgb(3,7,18)
          `}}
        />
        <motion.div
          className="relative max-w-3xl mx-auto space-y-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={stagger}
        >
          <motion.div variants={fadeUp} className="text-center space-y-3">
            <h2 className="text-2xl sm:text-3xl font-bold leading-snug">
              <span className="text-gradient">手段</span>ばかり、<br className="sm:hidden" />
              追いかけていませんか？
            </h2>
          </motion.div>

          <motion.div variants={fadeUp} className="text-center space-y-4">
            <p className="text-base leading-relaxed">
              写真は残した。メモも取った。<br />
              でも、<span className="font-semibold">あの時どう感じたか</span>は思い出せない。
            </p>
            <p className="text-base leading-relaxed">
              誰かに話したい。でも、<span className="font-semibold">話せない夜</span>がある。
            </p>
            <p className="text-base font-semibold text-muted-foreground">
              その気持ち、どこに置いていますか？
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* ═══════════════════════════════════════════
          03 — Solution：目的は「自己理解」
      ═══════════════════════════════════════════ */}
      <section className="relative py-20 px-4 overflow-hidden">
        {/* Light mesh */}
        <div
          className="absolute inset-0 dark:hidden pointer-events-none"
          style={{ background: `
            radial-gradient(ellipse 70% 70% at 5% 40%, rgba(165,180,252,0.25), transparent),
            radial-gradient(ellipse 60% 60% at 80% 70%, rgba(196,181,253,0.2), transparent),
            white
          `}}
        />
        {/* Dark mesh */}
        <div
          className="absolute inset-0 hidden dark:block pointer-events-none"
          style={{ background: `
            radial-gradient(ellipse 70% 70% at 5% 40%, rgba(67,56,202,0.1), transparent),
            radial-gradient(ellipse 60% 60% at 80% 70%, rgba(88,28,135,0.1), transparent),
            rgb(3,7,18)
          `}}
        />

        <motion.div
          className="relative max-w-4xl mx-auto space-y-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={stagger}
        >
          <motion.div variants={fadeUp} className="text-center space-y-3">
            <h2 className="text-2xl sm:text-4xl font-bold tracking-tight leading-snug">
              目的は<span className="text-gradient">「自己理解」</span>。<br className="sm:hidden" />
              あなたがやることは、話すだけ。
            </h2>
          </motion.div>

          {/* Before → After mock */}
          <motion.div variants={fadeUp} className="max-w-2xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Before: raw transcript */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 p-5 space-y-3">
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">あなたが話した内容</p>
              <div className="flex items-start gap-2">
                <Mic className="h-4 w-4 text-purple-500 mt-0.5 shrink-0" />
                <p className="text-sm leading-relaxed text-muted-foreground">
                  今日はちょっと嬉しいことがあって、帰り道に夕焼けがすごいきれいで、なんか気持ちが軽くなった気がする…
                </p>
              </div>
            </div>
            {/* After: AI output — actual app style */}
            <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-white dark:bg-gray-950 p-5 space-y-3 shadow-sm">
              <p className="text-[10px] font-medium text-blue-500 uppercase tracking-wider flex items-center gap-1">
                <Brain className="h-3 w-3" />
                AIが整理した結果
              </p>
              {/* Entry header */}
              <div className="flex items-start gap-2">
                <span className="text-2xl">😊</span>
                <div className="min-w-0">
                  <h4 className="text-base font-bold leading-snug">夕焼けに気づけた日</h4>
                  <p className="text-[10px] text-muted-foreground mt-0.5">2026年2月22日 (土) 19:30　嬉しい</p>
                </div>
              </div>
              {/* Summary */}
              <p className="text-xs leading-relaxed text-blue-600 dark:text-blue-400">
                帰り道の夕焼けに心を動かされた。小さな美しさに気づける余裕が、気持ちを軽くしてくれた。
              </p>
              {/* AI comment */}
              <p className="text-[10px] text-muted-foreground">
                🫧 小さな幸せに気づける感性、素敵ですね。
              </p>
              {/* Tags */}
              <div className="flex flex-wrap gap-1.5">
                <span className="text-[10px] px-2 py-0.5 rounded-full border border-gray-200 dark:border-gray-700 text-muted-foreground">日常</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full border border-gray-200 dark:border-gray-700 text-muted-foreground">散歩</span>
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-950/30 text-green-700 dark:text-green-400">感情 +70%</span>
              </div>
            </div>
          </motion.div>

          <motion.p variants={fadeUp} className="text-center text-lg font-medium leading-relaxed">
            話すだけで、AIが整理してくれる。
          </motion.p>

          <motion.div variants={fadeUp} className="text-center">
            <Link to="/login">
              <Button size="lg" className="gap-2 text-base px-10 rounded-full shadow-md bg-indigo-600 hover:bg-indigo-700 text-white">
                Hitorigotoをはじめる
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* ═══════════════════════════════════════════
          04 — Proof：30日で思考のクセが見えてくる
      ═══════════════════════════════════════════ */}
      <section className="relative py-20 px-4 overflow-hidden">
        {/* Light mesh */}
        <div
          className="absolute inset-0 dark:hidden pointer-events-none"
          style={{ background: `
            radial-gradient(ellipse 60% 60% at 80% 20%, rgba(249,168,212,0.25), transparent),
            radial-gradient(ellipse 70% 60% at 15% 75%, rgba(196,181,253,0.2), transparent),
            white
          `}}
        />
        {/* Dark mesh */}
        <div
          className="absolute inset-0 hidden dark:block pointer-events-none"
          style={{ background: `
            radial-gradient(ellipse 60% 60% at 80% 20%, rgba(112,26,117,0.1), transparent),
            radial-gradient(ellipse 70% 60% at 15% 75%, rgba(88,28,135,0.1), transparent),
            rgb(3,7,18)
          `}}
        />

        <motion.div
          className="relative max-w-3xl mx-auto space-y-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={stagger}
        >
          <motion.div variants={fadeUp} className="text-center space-y-3">
            <h2 className="text-2xl sm:text-3xl font-bold leading-snug">
              続けるほど、<br className="sm:hidden" />
              <span className="text-gradient">見えるもの</span>が変わる。
            </h2>
          </motion.div>

          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
            <div className="text-center">
              <p className="text-2xl sm:text-3xl font-bold text-indigo-600 dark:text-indigo-400">初日</p>
              <p className="text-sm font-medium text-gradient mt-1">話すだけでスッキリ</p>
            </div>
            <div className="hidden sm:block w-8 h-px bg-border" />
            <div className="text-center">
              <p className="text-2xl sm:text-3xl font-bold text-indigo-600 dark:text-indigo-400">3日</p>
              <p className="text-sm font-medium text-gradient mt-1">気分の波が見える</p>
            </div>
            <div className="hidden sm:block w-8 h-px bg-border" />
            <div className="text-center">
              <p className="text-2xl sm:text-3xl font-bold text-indigo-600 dark:text-indigo-400">30日</p>
              <p className="text-sm font-medium text-gradient mt-1">思考のパターンが見える</p>
            </div>
            <div className="hidden sm:block w-8 h-px bg-border" />
            <div className="text-center">
              <p className="text-2xl sm:text-3xl font-bold text-indigo-600 dark:text-indigo-400">1年</p>
              <p className="text-sm font-medium text-gradient mt-1">自分の変化が見える</p>
            </div>
          </motion.div>

          {/* Mobile: horizontal scroll carousel / Desktop: grid */}
          <motion.div
            variants={fadeUp}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4 sm:mx-0 sm:px-0 sm:pb-0 sm:grid sm:grid-cols-3 sm:gap-6 sm:overflow-visible"
            style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
          >
            {/* 感情トレンド + 気分の分布（タブ切り替え） */}
            <DashboardMockCard />

            {/* テーマの可視化 */}
            <div className="min-w-[80vw] snap-center sm:min-w-0">
              <Card className="h-full rounded-xl shadow-sm hover:shadow-md transition-shadow bg-card overflow-hidden">
                <div className="px-5 pt-5 pb-3 bg-gradient-to-b from-pink-50/50 to-transparent dark:from-pink-950/20">
                  <p className="text-[10px] text-muted-foreground mb-2">ワードクラウド</p>
                  <div className="flex flex-wrap gap-x-2 gap-y-0.5 justify-center min-h-[96px] items-center leading-tight">
                    <span className="text-2xl font-bold text-gray-800 dark:text-gray-200">仕事</span>
                    <span className="text-lg font-bold text-indigo-500 dark:text-indigo-400">家族</span>
                    <span className="text-lg font-bold text-gray-700 dark:text-gray-300">成長</span>
                    <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">挑戦</span>
                    <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">学び</span>
                    <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">健康</span>
                    <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">目標</span>
                    <span className="text-xs text-gray-500 dark:text-gray-500">趣味</span>
                    <span className="text-xs text-gray-500 dark:text-gray-500">感謝</span>
                    <span className="text-xs text-gray-500 dark:text-gray-500">休息</span>
                    <span className="text-xs text-gray-500 dark:text-gray-500">友人</span>
                    <span className="text-xs text-gray-500 dark:text-gray-500">将来</span>
                  </div>
                  <p className="text-[9px] text-muted-foreground text-center mt-1">大きく濃い言葉ほど、よく登場しています</p>
                </div>
                <CardContent className="pt-4 space-y-2">
                  <h3 className="text-lg font-semibold">{proofValues[1].title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{proofValues[1].desc}</p>
                </CardContent>
              </Card>
            </div>

            {/* 意味で探せる検索 */}
            <div className="min-w-[80vw] snap-center sm:min-w-0">
              <Card className="h-full rounded-xl shadow-sm hover:shadow-md transition-shadow bg-card overflow-hidden">
                <div className="px-5 pt-5 pb-3 bg-gradient-to-b from-indigo-50/50 to-transparent dark:from-indigo-950/20">
                  <div className="space-y-2">
                    {/* Search bar — matches real app */}
                    <div className="flex gap-1.5">
                      <div className="relative flex-1">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 pl-7 pr-7 py-1.5 text-xs text-foreground">
                          カフェ
                        </div>
                        <X className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                      </div>
                      <div className="w-7 h-7 rounded-md border border-gray-200 dark:border-gray-700 flex items-center justify-center shrink-0">
                        <Search className="h-3 w-3 text-muted-foreground" />
                      </div>
                    </div>
                    {/* Results count */}
                    <p className="text-[10px] text-muted-foreground">3件 (検索結果)</p>
                    {/* Result cards — standard mode */}
                    <div className="space-y-1.5">
                      {[
                        { emoji: '☕', title: '駅前の喫茶店でゆっくり読書', score: 72, date: '3/10', color: 'bg-green-400' },
                        { emoji: '😌', title: 'コーヒー片手に公園で考え事', score: 65, date: '3/5', color: 'bg-green-400' },
                        { emoji: '🍰', title: '友達と珈琲店でおしゃべり', score: 61, date: '2/22', color: 'bg-yellow-400' },
                      ].map((r) => (
                        <div key={r.title} className="rounded-lg border border-gray-100 dark:border-gray-800 overflow-hidden bg-white dark:bg-gray-900">
                          <div className={`h-0.5 ${r.color}`} />
                          <div className="px-2.5 py-2 flex items-center gap-2">
                            <span className="text-base shrink-0">{r.emoji}</span>
                            <span className="text-[11px] font-medium truncate flex-1">{r.title}</span>
                            <span className="text-[9px] bg-indigo-100 dark:bg-indigo-950/30 text-indigo-600 dark:text-indigo-400 rounded-full px-1.5 py-0.5 whitespace-nowrap shrink-0">{r.score}%</span>
                            <span className="text-[9px] text-muted-foreground whitespace-nowrap shrink-0">{r.date}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <CardContent className="pt-4 space-y-2">
                  <h3 className="text-lg font-semibold">{proofValues[2].title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{proofValues[2].desc}</p>
                </CardContent>
              </Card>
            </div>


            {/* AIインサイト（気づき・つながり） */}
            <InsightMockCard />

          </motion.div>

          {/* Scroll hint dots (mobile only) */}
          <div className="flex justify-center gap-1.5 sm:hidden">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="w-1.5 h-1.5 rounded-full bg-foreground/20" />
            ))}
          </div>
        </motion.div>
      </section>

      {/* ═══════════════════════════════════════════
          05 — Why voice：「書く」を捨てたから、続く
      ═══════════════════════════════════════════ */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-gray-900 via-slate-900 to-purple-950 overflow-hidden">
        {/* Decorative glow */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-purple-600/10 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-pink-600/10 blur-3xl pointer-events-none" />

        <motion.div
          className="relative max-w-xl mx-auto space-y-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={stagger}
        >
          <motion.div variants={fadeUp} className="text-center space-y-3">
            <h2 className="text-2xl sm:text-3xl font-bold leading-snug text-white">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">「書く」を捨てた</span>から、続く。
            </h2>
            <p className="text-sm text-gray-400">
              声は、手書きの4倍速い。
            </p>
          </motion.div>

          <motion.div variants={fadeUp} className="space-y-5">
            {speedData.map((item) => (
              <div key={item.label} className="space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className={item.highlight ? 'font-semibold text-purple-300' : 'text-gray-400'}>
                    {item.label}
                  </span>
                  <span className="text-gray-500 text-xs">{item.range}</span>
                </div>
                <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                  <motion.div
                    className={`h-full rounded-full ${item.highlight
                      ? 'bg-gradient-to-r from-purple-500 to-pink-400'
                      : 'bg-white/15'
                    }`}
                    variants={growRight}
                    custom={item.width}
                  />
                </div>
              </div>
            ))}
          </motion.div>

          <motion.p variants={fadeUp} className="text-xs text-gray-500 text-center">
            出典: Ruan et al., Stanford University, 2016
          </motion.p>

          {/* Usage scenes */}
          <motion.div variants={fadeUp} className="grid grid-cols-2 gap-4">
            <div className="rounded-xl overflow-hidden border border-white/10">
              <div className="relative h-36 bg-gradient-to-br from-purple-200/40 via-indigo-100/50 to-pink-100/40">
                <img
                  src="/images/bedtime-scene.webp"
                  alt="布団の中でスマホに話しかける女性"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
              </div>
              <div className="p-4 text-center bg-white/5">
                <p className="text-sm font-medium text-gray-200">布団の中でも</p>
                <p className="text-xs text-gray-400 mt-1">考えなくていい。ただ、つぶやくだけ。</p>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden border border-white/10">
              <div className="relative h-36 bg-gradient-to-br from-sky-100/50 via-indigo-100/40 to-purple-200/40">
                <img
                  src="/images/walking-scene.webp"
                  alt="散歩しながらスマホに話しかける人"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
              </div>
              <div className="p-4 text-center bg-white/5">
                <p className="text-sm font-medium text-gray-200">歩きながらでも</p>
                <p className="text-xs text-gray-400 mt-1">ふと浮かんだ言葉を、そのまま残せる。</p>
              </div>
            </div>
          </motion.div>

          <motion.p variants={fadeUp} className="text-center text-base text-gray-300 leading-relaxed">
            速いだけじゃありません。<br />
            AIが自動で<span className="font-semibold text-white">整理・分析</span>までしてくれる。
          </motion.p>
        </motion.div>
      </section>

      {/* ═══════════════════════════════════════════
          06 — Pricing：無料ではじめる。もっと深めたくなったら。
      ═══════════════════════════════════════════ */}
      <section className="relative py-20 px-4 overflow-hidden">
        {/* Light mesh */}
        <div
          className="absolute inset-0 dark:hidden pointer-events-none"
          style={{ background: `
            radial-gradient(ellipse 80% 50% at 30% 0%, rgba(196,181,253,0.2), transparent),
            radial-gradient(ellipse 60% 50% at 70% 80%, rgba(232,121,249,0.15), transparent),
            white
          `}}
        />
        {/* Dark mesh */}
        <div
          className="absolute inset-0 hidden dark:block pointer-events-none"
          style={{ background: `
            radial-gradient(ellipse 80% 50% at 30% 0%, rgba(88,28,135,0.15), transparent),
            radial-gradient(ellipse 60% 50% at 70% 80%, rgba(112,26,117,0.1), transparent),
            rgb(3,7,18)
          `}}
        />

        <motion.div
          className="relative max-w-3xl mx-auto space-y-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={stagger}
        >
          <motion.div variants={fadeUp} className="text-center space-y-3">
            <h2 className="text-2xl sm:text-3xl font-bold leading-snug">
              <span className="text-gradient">無料</span>ではじめる。<br className="sm:hidden" />
              もっと深めたくなったら。
            </h2>
            <p className="text-sm text-muted-foreground">
              まずは無料プランで、声の日記を体験してみてください。
            </p>
          </motion.div>

          <motion.div variants={fadeUp} className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-2xl mx-auto">
            {/* Free Plan */}
            <Card className="rounded-2xl border border-border bg-card shadow-sm">
              <CardContent className="p-6 space-y-5">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">フリー</p>
                  <p className="text-3xl font-bold">¥0</p>
                  <p className="text-xs text-muted-foreground">ずっと無料</p>
                </div>
                <ul className="space-y-2.5 text-sm">
                  {[
                    '月10分の録音時間',
                    '辞書5件まで',
                    'カスタムタグ5個',
                    '振り返り月1回（直近1ヶ月）',
                    'テーマ1種類',
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/login" className="block">
                  <Button variant="outline" className="w-full rounded-full">
                    無料ではじめる
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Premium Plan */}
            <Card className="rounded-2xl border-2 border-purple-400 dark:border-purple-500 bg-card shadow-md relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-gradient-to-l from-purple-600 to-pink-500 text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl">
                おすすめ
              </div>
              <CardContent className="p-6 space-y-5">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-purple-600 dark:text-purple-400 flex items-center gap-1">
                    <Sparkles className="h-3.5 w-3.5" />
                    プレミアム
                  </p>
                  <div className="flex items-baseline gap-1">
                    <p className="text-3xl font-bold">¥780</p>
                    <span className="text-sm text-muted-foreground">/月</span>
                  </div>
                  <p className="text-xs text-muted-foreground">年払い ¥6,500（¥542/月相当・<span className="font-semibold text-purple-500">30%OFF</span>）</p>
                </div>
                <ul className="space-y-2.5 text-sm">
                  {[
                    '録音時間 無制限',
                    '辞書100件',
                    'カスタムタグ50個',
                    '振り返り月10回（全期間）',
                    '12テーマ全開放',
                    '画像添付',
                    '優先サポート',
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="h-4 w-4 mt-0.5 shrink-0 text-purple-500" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/login" className="block">
                  <Button className="w-full rounded-full bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white border-0">
                    プレミアムを始める
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </section>

      {/* ═══════════════════════════════════════════
          CTA — 自分を知る旅を、今日から。
      ═══════════════════════════════════════════ */}
      <section className="relative py-24 px-4 bg-gradient-to-br from-purple-600 via-purple-700 to-pink-600 overflow-hidden">
        {/* Decorative orbs */}
        <div className="absolute top-0 left-1/4 w-64 h-64 rounded-full bg-white/10 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-48 h-48 rounded-full bg-pink-400/20 blur-3xl pointer-events-none" />

        <motion.div
          className="relative max-w-md mx-auto text-center space-y-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={stagger}
        >
          <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl font-bold leading-snug text-white">
            今夜、Hitorigotoを<br />
            はじめてみませんか。
          </motion.h2>
          <motion.p variants={fadeUp} className="text-purple-100 leading-relaxed">
            無料。クレジットカード不要。
          </motion.p>
          <motion.div variants={fadeUp}>
            <Link to="/login">
              <Button size="lg" className="gap-2 text-base px-10 rounded-full shadow-lg bg-white text-purple-700 hover:bg-purple-50 border-0">
                Hitorigotoを、はじめてみる
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="border-t border-gray-200 dark:border-gray-800 py-8 px-4 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <img src="/logo.svg" alt="Hitorigoto" className="h-6 dark:hidden" />
            <img src="/logo-dark.svg" alt="Hitorigoto" className="h-6 hidden dark:block" />
          </div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:underline">プライバシーポリシー</Link>
            <Link to="/terms" className="hover:underline">利用規約</Link>
            <Link to="/commercial-transactions" className="hover:underline">特定商取引法</Link>
            <Link to="/support" className="hover:underline">サポート</Link>
          </div>
          <p>&copy; 2026 Hitorigoto. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
